/**
 * RHDesignTokens
 *
 * The single design-token source for the RH webclient. This file defines values only:
 * components opt in by referencing these tokens explicitly.
 *
 * Structure (top to bottom):
 *   1. RHColors         - every shared color, once. The single source of truth for color.
 *                         Change a hex here and it updates everywhere that references it.
 *   2. Scales           - RHSpacing, RHRadius, RHShadow, RHFont, RHTransition.
 *   3. Component tokens  - grouped, reusable component families:
 *                            RHButtons        (.compact .bar .tableAction .compactPrimary
 *                                              .excelExport .destructiveUtility .exitUtility .dialogClose)
 *                            RHNavbar         (.topBar .userMenu .bottomBar)
 *                            RHForms          (.textField .checkbox .radio .datePicker .dropdown
 *                                              .dropdownRow .fileUpload .fileList .fileItem .panel .field)
 *                            RHTables         (.dataView .header .row .tree)
 *                            RHBadges         (.notification .tooltip .filterChip .alertBanner)
 *                            RHDialogs        (.confirmation .cpvTree .share
 *                                              .askQuestion .evaluationNotes .question)
 *
 * Adding a new component:
 *   - Reuse RHColors / the scales first; only add a new palette entry for a genuinely new color.
 *   - A reusable component adds or extends the matching family above.
 *   - A one-off screen value is not a design token: keep it inline or put it in RHViewTokens.ts
 *     when shared by several cooperating view files.
 */


import {UIColor, UITextView} from "uicore-ts"


export type RHButtonVariantTokens = {
    textColor: UIColor
    backgroundColor: UIColor
    backgroundHoverColor: UIColor
    backgroundActiveColor: UIColor
    borderColor: UIColor
    borderHoverColor: UIColor
    borderActiveColor: UIColor
    gradient: string
    gradientHover: string
    gradientActive: string
    shadow: string
    shadowHover: string
    shadowActive: string
    focusOutline?: string
}


// =============================================================================
//  RHColors - single source of truth for every color value in the RH design system.
//  Component tokens below reference these; change a color here and it updates everywhere.
// =============================================================================

export class RHColors {

    // Brand blue
    static get primaryBlue() { return new UIColor("#0065b2") }
    static get primaryBlueHover() { return new UIColor("#005a9e") }
    static get primaryBlueActive() { return new UIColor("#004d8c") }
    static get messageBlue() { return new UIColor("#055592") }
    static get messageBlueActive() { return new UIColor("#044a7d") }
    static get bootstrapBlue() { return new UIColor("#0d6efd") }
    static get primaryBlueSurface() { return new UIColor("#e6f0f8") }
    static get infoBlueSurface() { return new UIColor("#e0f2fe") }
    static get primaryBlueSurfaceIcon() { return new UIColor("#e8f3fb") }
    static get tableHeaderBlueSurface() { return new UIColor("#eef4fb") }
    static get tableRowHoverBlueSurface() { return new UIColor("#eaf3ff") }
    static get menuHoverBlueSurface() { return new UIColor("#eaf2fb") }
    static get messageBlueSurfaceHover() { return new UIColor("#f1f7fb") }
    static get messageBlueSurfacePressed() { return new UIColor("#e1edf7") }
    static get primaryBlueSurfaceFaint() { return new UIColor("#f7fbff") }

    // Success green
    static get successGreen() { return new UIColor("#10b981") }
    static get successGreenHover() { return new UIColor("#0d9d6f") }
    static get successGreenDark() { return new UIColor("#059669") }
    static get successGreenChipText() { return new UIColor("#047857") }
    static get successGreenTextDeep() { return new UIColor("#065f46") }
    static get successGreenTextDark() { return new UIColor("#14532d") }
    static get messageGreen() { return new UIColor("#276e40") }
    static get messageGreenActive() { return new UIColor("#1f5a34") }
    static get successGreenClassic() { return new UIColor("#28a745") }
    static get successGreenClassicBorder() { return new UIColor("#218838") }
    static get successGreenBadgeText() { return new UIColor("#3aa772") }
    static get successGreenSurface() { return new UIColor("#dcfce7") }
    static get successGreenSurfaceLight() { return new UIColor("#d1fae5") }
    static get successGreenSurfaceHover() { return new UIColor("#bbf7d0") }
    static get successGreenSurfacePressed() { return new UIColor("#a7f3d0") }
    static get successGreenSurfaceFaint() { return new UIColor("#ecfdf5") }
    static get tealMintSurface() { return new UIColor("#def7ec") }
    static get messageGreenSurfaceHover() { return new UIColor("#f2f8f1") }
    static get messageGreenSurfaceActive() { return new UIColor("#e2f0e0") }

    // Danger
    static get dangerRed() { return new UIColor("#dc3545") }
    static get dangerBadgePink() { return new UIColor("#e83258") }
    static get dangerBadgeText() { return new UIColor("#c81e45") }
    static get dangerTextSoft() { return new UIColor("#9b2c2c") }
    static get dangerCoralText() { return new UIColor("#b44b4b") }
    static get dangerMaroonText() { return new UIColor("#3f1d24") }
    static get dangerPinkSurface() { return new UIColor("#ffe4e6") }

    // Warning
    static get warningAmber() { return new UIColor("#f59e0b") }
    static get warningAmberHover() { return new UIColor("#e08e08") }
    static get warningAmberActive() { return new UIColor("#c87f07") }
    static get warningAmberBorderActive() { return new UIColor("#b87406") }
    static get warningOrange() { return new UIColor("#f97316") }
    static get warningOrangeDark() { return new UIColor("#ea580c") }
    static get warningAmberSurface() { return new UIColor("#fbefc9") }
    static get warningAmberSurfaceHover() { return new UIColor("#f8e7ba") }
    static get warningAmberSurfacePressed() { return new UIColor("#f2ddb0") }
    static get warningAmberBorder() { return new UIColor("#ecd79f") }
    static get warningAmberBorderHover() { return new UIColor("#e6ce90") }
    static get warningAmberBorderPressed() { return new UIColor("#dcc27e") }
    static get warningAmberSurfaceDraft() { return new UIColor("#fff7df") }
    static get warningAmberBorderDraft() { return new UIColor("#f2d27a") }
    static get warningAmberText() { return new UIColor("#6f5712") }
    static get warningAmberTextDeep() { return new UIColor("#5f4b12") }
    static get winnerGold() { return new UIColor("#b8860b") }
    static get ratingGold() { return new UIColor("#f5b301") }
    static get statisticsChartSecondary() { return new UIColor("#d97706") }
    static get offererTopBar() { return new UIColor("#b87209") }
    static get offererBottomBar() { return new UIColor("#fdf8ee") }

    // Indigo
    static get indigoSurface() { return new UIColor("#eef2ff") }
    static get indigoBorder() { return new UIColor("#c7d2fe") }
    static get indigoText() { return new UIColor("#3730a3") }
    static get indigoStrong() { return new UIColor("#4f46e5") }

    // Neutral text
    static get black() { return new UIColor("#000000") }
    static get textPrimary() { return new UIColor("#2c2c2c") }
    static get textHeading() { return new UIColor("#1f2937") }
    static get textTableHeading() { return new UIColor("#243447") }
    static get textBootstrap() { return new UIColor("#212529") }
    static get textNearBlack() { return new UIColor("#222222") }
    static get footerBackground() { return new UIColor("#4a4a49") }
    static get slateDarkest() { return new UIColor("#374151") }
    static get slateDark() { return new UIColor("#475569") }
    static get slateMedium() { return new UIColor("#64748b") }
    static get slateLight() { return new UIColor("#94a3b8") }
    static get slateLightest() { return new UIColor("#cbd5e1") }
    static get grayMedium() { return new UIColor("#6b7280") }
    static get grayLight() { return new UIColor("#d1d5db") }
    static get textMuted() { return new UIColor("#6c757d") }
    static get textSecondary() { return new UIColor("#6c6c6c") }
    static get textTimestamp() { return new UIColor("#666666") }
    static get statisticsExplanationText() { return new UIColor("#5f6368") }
    static get textPlaceholder() { return new UIColor("#9ca3af") }
    static get tooltipText() { return new UIColor("#f0f0f5") }

    // Borders
    static get borderLight() { return new UIColor("#e5e7eb") }
    static get borderSlate() { return new UIColor("#e2e8f0") }
    static get borderClassic() { return new UIColor("#e0e0e0") }
    static get borderSoft() { return new UIColor("#e8eaed") }
    static get borderMuted() { return new UIColor("#dee2e6") }
    static get borderField() { return new UIColor("#d0d5dd") }
    static get borderLightCool() { return new UIColor("#e5e8eb") }
    static get borderLightHover() { return new UIColor("#dde1e5") }
    static get borderLightPressed() { return new UIColor("#d6dbe0") }
    static get borderDisabled() { return new UIColor("#d7dde5") }
    static get borderBlueFaint() { return new UIColor("#d7e5f2") }
    static get statisticsChartTooltipBorder() { return new UIColor("#dbe3ec") }
    static get statisticsChartDataZoomBorder() { return new UIColor("#d7e4f0") }
    static get statisticsChartDataZoomHandleBorder() { return new UIColor("#8ab7dd") }
    static get borderHeaderTint() { return new UIColor("#d8dde6") }
    static get borderRowFaint() { return new UIColor("#eaecf0") }
    static get borderMenuSeparator() { return new UIColor("#eceff1") }

    // Surfaces
    static get white() { return new UIColor("#ffffff") }
    static get pageBackground() { return new UIColor("#f6f4f2") }
    static get surfaceMuted() { return new UIColor("#f8f9fa") }
    static get surfaceSubtle() { return new UIColor("#f1f3f5") }
    static get surfaceCool() { return new UIColor("#f8fafc") }
    static get surfaceNearWhite() { return new UIColor("#f9f9f9") }
    static get surfaceLightHover() { return new UIColor("#f6f7f8") }
    static get surfacePanel() { return new UIColor("#f3f4f6") }
    static get surfaceRowOdd() { return new UIColor("#fbfcfe") }
    static get statisticsTableSubRowBackground() { return new UIColor("#eef5fb") }
    static get surfaceDisabled() { return new UIColor("#e9ecef") }
    static get surfaceDisabledCool() { return new UIColor("#eef2f6") }

    // Overlays
    static get transparent() { return new UIColor("transparent") }
    static get grayLegacy() { return new UIColor("rgb(100, 100, 100)") }
    static get blackAlpha30() { return new UIColor("rgba(0, 0, 0, 0.3)") }
    static get blackAlpha08() { return new UIColor("rgba(0, 0, 0, 0.08)") }
    static get blackAlpha15() { return new UIColor("rgba(0, 0, 0, 0.15)") }
    static get blackAlpha40() { return new UIColor("rgba(0, 0, 0, 0.4)") }
    static get blackAlpha04() { return new UIColor("rgba(0,0,0,0.04)") }
    static get whiteAlpha98() { return new UIColor("rgba(255, 255, 255, 0.98)") }
    static get whiteAlpha95() { return new UIColor("rgba(255, 255, 255, 0.95)") }
    static get whiteAlpha80() { return new UIColor("rgba(255, 255, 255, 0.8)") }
    static get whiteAlpha75() { return new UIColor("rgba(255, 255, 255, 0.75)") }
    static get whiteAlpha50() { return new UIColor("rgba(255, 255, 255, 0.50)") }
    static get whiteAlpha20() { return new UIColor("rgba(255, 255, 255, 0.2)") }
    static get primaryBlueAlpha42() { return new UIColor("rgba(0, 101, 178, 0.42)") }
    static get primaryBlueAlpha36() { return new UIColor("rgba(0, 101, 178, 0.36)") }
    static get primaryBlueAlpha30() { return new UIColor("rgba(0, 101, 178, 0.30)") }
    static get primaryBlueAlpha22() { return new UIColor("rgba(0, 101, 178, 0.22)") }
    static get primaryBlueAlpha18() { return new UIColor("rgba(0, 101, 178, 0.18)") }
    static get primaryBlueAlpha16() { return new UIColor("rgba(0, 101, 178, 0.16)") }
    static get blueBootstrapAlpha03() { return new UIColor("rgba(13, 110, 253, 0.03)") }
    static get blueBootstrapAlpha05() { return new UIColor("rgba(13, 110, 253, 0.05)") }
    static get blueGlass() { return new UIColor("rgba(227, 239, 250, 0.98)") }
    static get successGreenAlpha42() { return new UIColor("rgba(16, 185, 129, 0.42)") }
    static get successGreenAlpha34() { return new UIColor("rgba(16, 185, 129, 0.34)") }
    static get successGreenAlpha15() { return new UIColor("rgba(16, 185, 129, 0.15)") }
    static get successGreenDarkAlpha20() { return new UIColor("rgba(5, 150, 105, 0.2)") }
    static get greenBorderAlpha34() { return new UIColor("rgba(34, 197, 94, 0.34)") }
    static get greenGlass() { return new UIColor("rgba(236, 253, 245, 0.98)") }
    static get greenGlassMint() { return new UIColor("rgba(240, 252, 246, 0.98)") }
    static get softDangerFillAlpha15() { return new UIColor("rgba(232, 111, 111, 0.15)") }
    static get softDangerFillAlpha20() { return new UIColor("rgba(232, 111, 111, 0.20)") }
    static get softDangerFillAlpha24() { return new UIColor("rgba(232, 111, 111, 0.24)") }
    static get softDangerBorderAlpha22() { return new UIColor("rgba(214, 83, 83, 0.22)") }
    static get softDangerBorderAlpha26() { return new UIColor("rgba(214, 83, 83, 0.26)") }
    static get softDangerBorderAlpha30() { return new UIColor("rgba(214, 83, 83, 0.30)") }
    static get softDangerBorderAlpha34() { return new UIColor("rgba(214, 83, 83, 0.34)") }
    static get badgePinkBorderAlpha25() { return new UIColor("rgba(232, 50, 88, 0.25)") }
    static get pinkGlass() { return new UIColor("rgba(255, 244, 244, 0.96)") }
    static get slateGlass() { return new UIColor("rgba(248, 250, 252, 0.98)") }
    static get slateAlpha95() { return new UIColor("rgba(203, 213, 225, 0.95)") }
    static get slateAlpha92() { return new UIColor("rgba(203, 213, 225, 0.92)") }
    static get dividerAlpha50() { return new UIColor("rgba(224, 224, 224, 0.5)") }
    static get dividerAlpha70() { return new UIColor("rgba(224, 224, 224, 0.7)") }
    static get tooltipSurface() { return new UIColor("rgba(28, 28, 42, 0.97)") }
    static get navyAlpha40() { return new UIColor("rgba(26, 34, 85, 0.40)") }
    static get maroonAlpha40() { return new UIColor("rgba(68, 10, 17, 0.40)") }

}



// =============================================================================
//  Spacing
// =============================================================================

export const RHSpacing = {
    
    /** 4px */
    tiny: 4,
    
    /** 8px */
    small: 8,
    
    /** 16px */
    medium: 16,
    
    /** 24px */
    large: 24,
    
    /** 32px */
    xLarge: 32,
    
    /** 48px */
    xxLarge: 48
    
} as const


// =============================================================================
//  Border Radius
// =============================================================================

export const RHRadius = {

    /** 6px - subtle rounding for inputs and small chips. */
    small: 6,

    /** 8px - default rounding for cards and buttons. */
    medium: 8,

    /** 12px - medium rounding for panels and dialogs. */
    large: 12,

    /** 16px - large rounding for broad surfaces. */
    xLarge: 16

} as const


// =============================================================================
//  Shadows
// =============================================================================

export const RHShadow = {

    /** Subtle shadow for cards at rest. */
    subtle: "0 1px 3px rgba(0, 0, 0, 0.05), 0 2px 8px rgba(0, 0, 0, 0.04)",

    /** Default shadow for elevated cards and dropdowns. */
    medium: "0 4px 16px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.05)",

    /** Prominent shadow for modals and popovers. */
    strong: "0 8px 32px rgba(0, 0, 0, 0.10), 0 2px 8px rgba(0, 0, 0, 0.06)"

} as const


// =============================================================================
//  Typography
// =============================================================================

export const RHFont = {

    /** Source Sans Pro gives the RH UI a softer official tone; Inter/Comfortaa stay as fallbacks. */
    familyBase: "'Source Sans Pro', 'Inter', 'Comfortaa', sans-serif",
    familyHeading: "'Source Sans Pro', 'Exo', sans-serif",

    sizeXSmall: "0.75rem",     // 12px
    sizeSmall: "0.875rem",    // 14px
    sizeMedium: "1rem",      // 16px
    sizeLarge: "1.125rem",    // 18px
    sizeXLarge: "1.25rem",     // 20px
    sizeXXLarge: "1.5rem",     // 24px
    sizeXXXLarge: "2rem",       // 32px

    weightLight: "300",
    weightRegular: "400",
    weightMedium: "500",
    weightSemibold: "600",
    weightBold: "700",
    weightExtraBold: "800",

    lineHeightBase: "1.5",
    lineHeightHeading: "1.3"

} as const


// =============================================================================
//  Transitions
// =============================================================================

export const RHTransition = {
    
    /** 150ms ease - hover states, micro-interactions */
    fast: "150ms ease",
    
    /** 250ms ease - general UI transitions */
    base: "250ms ease",
    
    /** 350ms ease - larger movements, panel slides */
    slow: "350ms ease"
    
} as const


// =============================================================================
//  RHNavbar
// =============================================================================

export const RHNavbar = {

    // topBar
    topBarTopSectionHeight: 104,
    topBarBottomSectionHeight: 56,
    topBarLogButtonWidth: 150,
    topBarLogButtonHeight: 40,
    topBarHideLoginButtonOnLoginView: true,
    topBarTopControlButtonGap: RHSpacing.small,
    topBarFeedbackButtonWidth: 160,
    topBarFeedbackButtonHeight: 36,
    topBarTitleFontSize: 14,
    topBarTitleFontFamily: "'Roboto Condensed', sans-serif",
    topBarTitleLetterSpacing: "0.5px",
    topBarTitleTextTransform: "uppercase",
    topBarTitleInitialFontSize: "1.4em",
    topBarTitleInitialFontWeight: RHFont.weightLight,
    topBarTitleCompactTextLength: 42,
    topBarTitleCompactFontSize: 12,
    topBarTitleCompactLetterSpacing: "0.1em",
    topBarTitleWidthSafetyBuffer: 36,
    topBarTitleUserInfoReserveWidth: 260,
    topBarUserNameFontSize: 12,
    topBarUserNameFontFamily: RHFont.familyBase,
    topBarUserInfoLabelHeight: 28,
    topBarUserInfoBlockHeight: 84,
    topBarTopBarBackground: RHColors.primaryBlue,
    topBarBottomBarBackground: RHColors.white,
    topBarTitleColor: RHColors.white,
    topBarUserNameColor: RHColors.white.colorWithAlpha(0.9),
    topBarCompanyLabelColor: RHColors.warningAmber,
    topBarCompanyLabelFontFamily: "'Inter', 'Source Sans Pro', sans-serif",
    topBarShadow: "0px 4px 10px rgba(0,0,0,0.2)",

    // Compact role-mode segmented control on the unchanged primary-blue top bar.
    topBarModeToggle: {
        buttonWidth: 128,
        buttonHeight: 40,
        segmentGap: 0,
        leadingSegmentBorderRadius: `${RHRadius.medium}px 0 0 ${RHRadius.medium}px`,
        trailingSegmentBorderRadius: `0 ${RHRadius.medium}px ${RHRadius.medium}px 0`,
        outerBorderStyle: `2px solid ${RHColors.white.stringValue}`,
        transition: `background-color ${RHTransition.fast}, color ${RHTransition.fast}, box-shadow ${RHTransition.fast}`,
        focusOutline: `3px solid ${RHColors.whiteAlpha75.stringValue}`,
        focusOutlineOffset: "2px",
        normal: {
            textColor: RHColors.white,
            backgroundColor: RHColors.primaryBlueActive,
            boxShadow: `inset 0 0 0 1px ${RHColors.whiteAlpha20.stringValue}`
        },
        hovered: {
            textColor: RHColors.white,
            backgroundColor: RHColors.primaryBlueHover,
            boxShadow: `inset 0 0 0 1px ${RHColors.whiteAlpha50.stringValue}`
        },
        highlighted: {
            textColor: RHColors.white,
            backgroundColor: RHColors.primaryBlueActive,
            boxShadow: `inset 0 0 0 1px ${RHColors.whiteAlpha75.stringValue}`
        },
        selected: {
            textColor: RHColors.textHeading,
            backgroundColor: RHColors.warningAmber,
            boxShadow: `inset 0 0 0 1px ${RHColors.warningAmberBorderActive.stringValue}, 0 2px 6px ${RHColors.blackAlpha15.stringValue}`
        },
        selectedHighlighted: {
            textColor: RHColors.textHeading,
            backgroundColor: RHColors.warningAmberActive,
            boxShadow: `inset 0 0 0 1px ${RHColors.warningAmberBorderActive.stringValue}`
        }
    },

    // userMenu
    userMenuPanelBackgroundColor: RHColors.white.stringValue,
    userMenuPanelBorderColor: RHColors.borderLight.stringValue,
    userMenuPanelBorderRadius: `${RHRadius.large}px`,
    userMenuPanelShadow: RHShadow.strong,
    userMenuPanelZIndex: "1000",
    userMenuPanelMinWidth: 200,
    userMenuPanelMaxWidth: 340,
    userMenuAnchorGap: 10,
    userMenuTailSize: 12,
    userMenuTailRightInset: 14,
    userMenuItemHeight: 44,
    userMenuItemHorizontalInset: 18,
    userMenuItemTextColor: RHColors.textPrimary.stringValue,
    userMenuItemHoverBackgroundColor: RHColors.menuHoverBlueSurface.stringValue,
    userMenuItemHoverTextColor: RHColors.primaryBlue.stringValue,
    userMenuItemSeparatorColor: RHColors.borderMenuSeparator.stringValue,
    userMenuFontFamily: RHFont.familyBase,
    userMenuItemFontSize: "0.95rem",
    userMenuItemFontWeight: RHFont.weightRegular,
    userMenuButtonIdleBackgroundColor: RHColors.blackAlpha08.stringValue,
    userMenuButtonHoverBackgroundColor: RHColors.blackAlpha15.stringValue,
    userMenuButtonActiveRingWidth: 2,
    userMenuButtonActiveRingColor: RHColors.warningAmber,
    userMenuButtonTransition:
        `background-color ${RHTransition.fast}, box-shadow ${RHTransition.fast}`,
    userMenuButtonBorderRadius: `${RHRadius.medium}px`,
    userMenuButtonHorizontalInset: 12,
    userMenuChevronColor: RHColors.whiteAlpha95.stringValue,
    userMenuChevronFontSize: "14px",
    userMenuChevronWidth: 16,
    userMenuButtonMaxWidth: 460,
    userMenuButtonLabelToChevronGap: 4,
    userMenuLabelWidthSafetyBuffer: 6,  // small extra width over the measured text so names never clip

    // bottomBar
    bottomBarBackgroundColor: RHColors.footerBackground,
    bottomBarContentBackgroundColor: RHColors.transparent,
    bottomBarTextColor: RHColors.white,
    bottomBarLinkColor: RHColors.white,
    bottomBarLinkHoverColor: RHColors.whiteAlpha80,
    bottomBarFontFamily: RHFont.familyBase,

}


// =============================================================================
//  RHButtons
// =============================================================================

const RH_BUTTON_COMPACT_HEIGHT = 40
const RH_BUTTON_COMPACT_FONT_SIZE = RHFont.sizeSmall
const RH_BUTTON_COMPACT_CONTENT_PADDING = RHSpacing.medium
const RH_BUTTON_DESTRUCTIVE_UTILITY_VARIANT = "softDanger" as const
const RH_BUTTON_EXIT_UTILITY_VARIANT = "softWarning" as const

export const RHButtons = {

    // base
    padding: "12px 24px",
    fontWeight: RHFont.weightMedium,
    fontFamily: RHFont.familyBase,
    fontSize: "1rem",
    borderRadius: `${RHRadius.medium}px`,
    lineHeight: "1.2",
    contentPadding: 24,
    rippleBackgroundColor: RHColors.whiteAlpha20.stringValue,
    rippleTransition: "transform 0.6s ease-out, opacity 0.4s ease-out",
    transition: "box-shadow 250ms ease, border-color 250ms ease, background-image 250ms ease, outline-color 250ms ease",
    focusOutline: "3px solid rgba(0, 101, 178, 0.25)",
    focusOutlineOffset: "2px",

    // variants (indexed by key: RHButtons[variant])
    primary: {
        textColor: RHColors.white,
        backgroundColor: RHColors.primaryBlue,
        backgroundHoverColor: RHColors.primaryBlueHover,
        backgroundActiveColor: RHColors.primaryBlueActive,
        borderColor: RHColors.primaryBlueHover,
        borderHoverColor: RHColors.primaryBlueActive,
        borderActiveColor: RHColors.primaryBlueActive,
        gradient: `linear-gradient(180deg, #0b78c2 0%, ${RHColors.primaryBlue.stringValue} 100%)`,
        gradientHover: `linear-gradient(180deg, #0870b8 0%, ${RHColors.primaryBlueHover.stringValue} 100%)`,
        gradientActive: `linear-gradient(180deg, ${RHColors.primaryBlueHover.stringValue} 0%, ${RHColors.primaryBlueActive.stringValue} 100%)`,
        shadow: "0 1px 2px rgba(0, 74, 130, 0.18), 0 4px 10px rgba(0, 101, 178, 0.14)",
        shadowHover: "0 2px 4px rgba(0, 74, 130, 0.22), 0 8px 18px rgba(0, 101, 178, 0.20)",
        shadowActive: "0 1px 2px rgba(0, 74, 130, 0.18)",
    },
    success: {
        textColor: RHColors.white,
        backgroundColor: RHColors.successGreen,
        backgroundHoverColor: RHColors.successGreenHover,
        backgroundActiveColor: RHColors.successGreenDark,
        borderColor: RHColors.successGreenHover,
        borderHoverColor: RHColors.successGreenDark,
        borderActiveColor: RHColors.successGreenDark,
        gradient: `linear-gradient(180deg, #18c78e 0%, ${RHColors.successGreen.stringValue} 100%)`,
        gradientHover: `linear-gradient(180deg, #13b981 0%, ${RHColors.successGreenHover.stringValue} 100%)`,
        gradientActive: `linear-gradient(180deg, ${RHColors.successGreenHover.stringValue} 0%, ${RHColors.successGreenDark.stringValue} 100%)`,
        shadow: "0 1px 2px rgba(6, 95, 70, 0.16), 0 4px 10px rgba(16, 185, 129, 0.14)",
        shadowHover: "0 2px 4px rgba(6, 95, 70, 0.20), 0 8px 18px rgba(16, 185, 129, 0.20)",
        shadowActive: "0 1px 2px rgba(6, 95, 70, 0.16)",
    },
    brandOrange: {
        textColor: RHColors.white,
        backgroundColor: RHColors.warningAmber,
        backgroundHoverColor: RHColors.warningAmberHover,
        backgroundActiveColor: RHColors.warningAmberActive,
        borderColor: RHColors.warningAmberHover,
        borderHoverColor: RHColors.warningAmberActive,
        borderActiveColor: RHColors.warningAmberBorderActive,
        gradient: `linear-gradient(180deg, #f7aa22 0%, ${RHColors.warningAmber.stringValue} 100%)`,
        gradientHover: `linear-gradient(180deg, #ee9d16 0%, ${RHColors.warningAmberHover.stringValue} 100%)`,
        gradientActive: `linear-gradient(180deg, #d98a07 0%, ${RHColors.warningAmberActive.stringValue} 100%)`,
        shadow: "0 1px 2px rgba(146, 64, 14, 0.16), 0 4px 10px rgba(245, 158, 11, 0.16)",
        shadowHover: "0 2px 4px rgba(146, 64, 14, 0.20), 0 8px 18px rgba(245, 158, 11, 0.22)",
        shadowActive: "0 1px 2px rgba(146, 64, 14, 0.16)",
    },
    softWarning: {
        textColor: RHColors.warningAmberText,
        backgroundColor: RHColors.warningAmberSurface,
        backgroundHoverColor: RHColors.warningAmberSurfaceHover,
        backgroundActiveColor: RHColors.warningAmberSurfacePressed,
        borderColor: RHColors.warningAmberBorder,
        borderHoverColor: RHColors.warningAmberBorderHover,
        borderActiveColor: RHColors.warningAmberBorderPressed,
        gradient: `linear-gradient(135deg, #fdf6de 0%, ${RHColors.warningAmberSurface.stringValue} 60%, #f5e7bc 100%)`,
        gradientHover: `linear-gradient(135deg, #fcf2d5 0%, ${RHColors.warningAmberSurfaceHover.stringValue} 60%, #f0dfab 100%)`,
        gradientActive: `linear-gradient(135deg, #f8ebca 0%, ${RHColors.warningAmberSurfacePressed.stringValue} 60%, #e9d39c 100%)`,
        shadow: "0 2px 6px rgba(214, 178, 90, 0.12)",
        shadowHover: "0 4px 10px rgba(214, 178, 90, 0.16)",
        shadowActive: "0 2px 4px rgba(214, 178, 90, 0.13)",
        focusOutline: "3px solid rgba(214, 178, 90, 0.32)",
    },
    quietWarning: {
        textColor: RHColors.warningAmberText,
        backgroundColor: RHColors.warningAmberSurface,
        backgroundHoverColor: RHColors.warningAmberSurfaceHover,
        backgroundActiveColor: RHColors.warningAmberSurfacePressed,
        borderColor: UIColor.transparentColor,
        borderHoverColor: UIColor.transparentColor,
        borderActiveColor: UIColor.transparentColor,
        gradient: `linear-gradient(135deg, #fdf6de 0%, ${RHColors.warningAmberSurface.stringValue} 60%, #f5e7bc 100%)`,
        gradientHover: `linear-gradient(135deg, #fcf2d5 0%, ${RHColors.warningAmberSurfaceHover.stringValue} 60%, #f0dfab 100%)`,
        gradientActive: `linear-gradient(135deg, #f8ebca 0%, ${RHColors.warningAmberSurfacePressed.stringValue} 60%, #e9d39c 100%)`,
        shadow: "0 2px 6px rgba(214, 178, 90, 0.12)",
        shadowHover: "0 4px 10px rgba(214, 178, 90, 0.16)",
        shadowActive: "0 2px 4px rgba(214, 178, 90, 0.13)",
        focusOutline: "none",
    },
    softDanger: {
        textColor: RHColors.dangerTextSoft,
        backgroundColor: RHColors.softDangerFillAlpha15,
        backgroundHoverColor: RHColors.softDangerFillAlpha20,
        backgroundActiveColor: RHColors.softDangerFillAlpha24,
        borderColor: RHColors.softDangerBorderAlpha22,
        borderHoverColor: RHColors.softDangerBorderAlpha30,
        borderActiveColor: RHColors.softDangerBorderAlpha34,
        gradient: "linear-gradient(135deg, rgba(255, 248, 248, 0.95) 0%, rgba(232, 111, 111, 0.15) 100%)",
        gradientHover: "linear-gradient(135deg, rgba(255, 244, 244, 0.97) 0%, rgba(232, 111, 111, 0.20) 100%)",
        gradientActive: "linear-gradient(135deg, rgba(255, 239, 239, 0.98) 0%, rgba(232, 111, 111, 0.24) 100%)",
        shadow: "0 2px 6px rgba(214, 83, 83, 0.08)",
        shadowHover: "0 4px 10px rgba(214, 83, 83, 0.12)",
        shadowActive: "0 2px 4px rgba(214, 83, 83, 0.10)",
        focusOutline: "3px solid rgba(214, 83, 83, 0.22)",
    },
    tableActionSuccess: {
        textColor: RHColors.white,
        backgroundColor: RHColors.successGreen,
        backgroundHoverColor: RHColors.successGreenDark,
        backgroundActiveColor: RHColors.successGreenDark,
        borderColor: RHColors.successGreenAlpha15,
        borderHoverColor: RHColors.successGreenDarkAlpha20,
        borderActiveColor: RHColors.successGreenDarkAlpha20,
        gradient: `linear-gradient(135deg, #34d399 0%, ${RHColors.successGreen.stringValue} 100%)`,
        gradientHover: `linear-gradient(135deg, ${RHColors.successGreen.stringValue} 0%, ${RHColors.successGreenDark.stringValue} 100%)`,
        gradientActive: `linear-gradient(135deg, ${RHColors.successGreen.stringValue} 0%, ${RHColors.successGreenDark.stringValue} 100%)`,
        shadow: "0 2px 6px rgba(16, 185, 129, 0.3)",
        shadowHover: "0 4px 12px rgba(16, 185, 129, 0.4)",
        shadowActive: "0 2px 4px rgba(52, 211, 153, 0.3)",
    },
    messageAnswer: {
        textColor: RHColors.messageBlue,
        backgroundColor: RHColors.white,
        backgroundHoverColor: RHColors.messageBlueSurfaceHover,
        backgroundActiveColor: RHColors.messageBlueSurfacePressed,
        borderColor: RHColors.messageBlue,
        borderHoverColor: RHColors.messageBlue,
        borderActiveColor: RHColors.messageBlueActive,
        gradient: `linear-gradient(135deg, ${RHColors.white.stringValue} 0%, #f8fbff 100%)`,
        gradientHover: `linear-gradient(135deg, ${RHColors.white.stringValue} 0%, #edf5fb 100%)`,
        gradientActive: "linear-gradient(135deg, #f5f9fd 0%, #e1edf7 100%)",
        shadow: "0 2px 6px rgba(5, 85, 146, 0.08)",
        shadowHover: "0 4px 10px rgba(5, 85, 146, 0.12)",
        shadowActive: "0 2px 4px rgba(5, 85, 146, 0.1)",
    },
    messageCopy: {
        textColor: RHColors.messageGreen,
        backgroundColor: RHColors.white,
        backgroundHoverColor: RHColors.messageGreenSurfaceHover,
        backgroundActiveColor: RHColors.messageGreenSurfaceActive,
        borderColor: RHColors.messageGreen,
        borderHoverColor: RHColors.messageGreen,
        borderActiveColor: RHColors.messageGreenActive,
        gradient: `linear-gradient(135deg, ${RHColors.white.stringValue} 0%, #fbfefb 100%)`,
        gradientHover: `linear-gradient(135deg, ${RHColors.white.stringValue} 0%, #eef7ed 100%)`,
        gradientActive: "linear-gradient(135deg, #f6fbf6 0%, #e2f0e0 100%)",
        shadow: "0 2px 6px rgba(39, 110, 64, 0.08)",
        shadowHover: "0 4px 10px rgba(39, 110, 64, 0.12)",
        shadowActive: "0 2px 4px rgba(39, 110, 64, 0.1)",
    },
    light: {
        textColor: RHColors.textNearBlack,
        backgroundColor: RHColors.surfaceMuted,
        backgroundHoverColor: RHColors.surfaceLightHover,
        backgroundActiveColor: RHColors.surfaceSubtle,
        borderColor: RHColors.borderLightCool,
        borderHoverColor: RHColors.borderLightHover,
        borderActiveColor: RHColors.borderLightPressed,
        gradient: `linear-gradient(135deg, #fbfbfc 0%, ${RHColors.surfaceMuted.stringValue} 60%, #f3f5f6 100%)`,
        gradientHover: `linear-gradient(135deg, #fbfbfc 0%, ${RHColors.surfaceLightHover.stringValue} 60%, #f0f2f4 100%)`,
        gradientActive: `linear-gradient(135deg, ${RHColors.surfaceMuted.stringValue} 0%, #f3f5f6 60%, #eceff2 100%)`,
        shadow: "0 2px 6px rgba(120, 120, 120, 0.06)",
        shadowHover: "0 4px 10px rgba(120, 120, 120, 0.09)",
        shadowActive: "0 2px 4px rgba(120, 120, 120, 0.08)",
    },
    link: {
        textColor: RHNavbar.topBarTopBarBackground,
        backgroundColor: UIColor.transparentColor,
        backgroundHoverColor: UIColor.transparentColor,
        backgroundActiveColor: UIColor.transparentColor,
        borderColor: UIColor.transparentColor,
        borderHoverColor: UIColor.transparentColor,
        borderActiveColor: UIColor.transparentColor,
        gradient: "none",
        gradientHover: "none",
        gradientActive: "none",
        shadow: "none",
        shadowHover: "none",
        shadowActive: "none",
    },

    // sizes / sub-variants
    compactHeight: RH_BUTTON_COMPACT_HEIGHT,
    compactFontSize: RH_BUTTON_COMPACT_FONT_SIZE,
    compactContentPadding: RH_BUTTON_COMPACT_CONTENT_PADDING,  // tighter intrinsic width than the default RHButton 24px
    borderPulseWidth: 3,
    borderPulseHalfDuration: 0.4,
    numberOfBorderPulses: 2,
    pageBackButtonWidth: 148,  // shared width for compact page-level “← Tagasi” actions
    barBorderRadius: `${RHRadius.medium}px`,
    barPadding: "8px 16px",
    barFontFamily: RHFont.familyBase,
    barFontWeight: RHFont.weightMedium,
    barFontSize: "1rem",
    tableActionWidth: 130,
    tableActionHeight: 36,
    tableActionContentPadding: RHSpacing.small,
    tableActionVariant: "primary" as const,
    tableActionFontSize: RHFont.sizeXSmall,
    tableActionTitleFontWeight: RHFont.weightMedium,  // compact but still clearly actionable
    compactPrimaryVariant: "primary" as const,
    compactPrimaryContentPadding: RH_BUTTON_COMPACT_CONTENT_PADDING,
    compactPrimaryFontSize: RH_BUTTON_COMPACT_FONT_SIZE,
    compactPrimaryHeight: RH_BUTTON_COMPACT_HEIGHT + 2,
    excelExportButtonSize: 30,
    excelExportIconSize: 20,
    excelExportContentPadding: 0,
    excelExportGenerateVariant: "success" as const,
    excelExportLoadingVariant: "secondary" as const,
    excelExportDisabledOpacity: 0.48,
    excelExportDisabledFilter: "grayscale(0.35) saturate(0.55)",
    excelExportDisabledCursor: "not-allowed",
    destructiveUtilityVariant: RH_BUTTON_DESTRUCTIVE_UTILITY_VARIANT,
    deleteIconHTML: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M3 6h18"/><path d="M19 6l-1 14H6L5 6"/><path d="M9 6V3h6v3"/><path d="M10 10v6M14 10v6"/></svg>`,
    exitUtilityVariant: RH_BUTTON_EXIT_UTILITY_VARIANT,
    dialogCloseTitle: "\u00D7",
    dialogCloseVariant: RH_BUTTON_DESTRUCTIVE_UTILITY_VARIANT,
    dialogCloseSize: 32,
    dialogCloseContentPadding: 0,
    dialogCloseFontSize: RHFont.sizeLarge,
    dialogCloseLineHeight: "1",
    dialogClosePadding: "0px",
    dialogCloseBorderRadius: `${RHRadius.medium}px`,

} satisfies Record<string, unknown> & {
    primary: RHButtonVariantTokens
    success: RHButtonVariantTokens
    brandOrange: RHButtonVariantTokens
    softWarning: RHButtonVariantTokens
    quietWarning: RHButtonVariantTokens
    softDanger: RHButtonVariantTokens
    tableActionSuccess: RHButtonVariantTokens
    messageAnswer: RHButtonVariantTokens
    messageCopy: RHButtonVariantTokens
    light: RHButtonVariantTokens
    link: RHButtonVariantTokens
}


// =============================================================================
//  RHForms
// =============================================================================

export const RHForms = {

    // textField
    textFieldFontFamily: RHFont.familyBase,
    textFieldFontSize: RHFont.sizeMedium,
    textFieldFontWeight: RHFont.weightRegular,
    textFieldLineHeight: "1.5",
    textFieldContentPadding: "0.375rem 0.75rem",
    textFieldBorderColor: RHColors.borderField,
    textFieldDisabledTextColor: RHColors.grayMedium,
    textFieldDisabledBackgroundColor: RHColors.surfaceDisabledCool,
    textFieldDisabledBorderColor: RHColors.borderDisabled,
    textFieldBorderRadius: `${RHRadius.medium}px`,
    textFieldTransition: "border-color .15s ease-in-out,box-shadow .15s ease-in-out",
    textFieldPlaceholderColor: RHColors.textMuted.stringValue,  // RHTextField injected placeholder CSS

    // checkbox
    checkboxContainerPadding: "2px 0",
    checkboxLabelColor: RHColors.textHeading,
    checkboxLabelFontSize: RHFont.sizeMedium,
    checkboxLabelFontWeight: RHFont.weightRegular,
    checkboxLabelMarginLeft: "10px",
    checkboxControlSize: 19,
    checkboxControlLeadingInset: 10,
    checkboxControlGap: 10,
    checkboxControlBorderRadius: `${RHRadius.small}px`,
    checkboxControlBackgroundColor: RHColors.white.stringValue,
    checkboxControlBackdropFilter: "none",
    checkboxControlBorderWidth: "2px",
    checkboxControlBorderColor: RHColors.slateLightest.stringValue,
    checkboxControlShadow: "0 1px 2px rgba(15, 23, 42, 0.07), 0 3px 8px rgba(15, 23, 42, 0.06)",
    checkboxControlTransition: "background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease",
    checkboxCheckedBackgroundColor: RHButtons.primary.backgroundColor.stringValue,
    checkboxCheckedBorderColor: RHButtons.primary.backgroundColor.stringValue,
    checkboxCheckedBackgroundSize: "96% 96%",
    checkboxCheckedIconColor: RHColors.white.stringValue,
    checkboxCheckedIconOutlineColor: RHColors.white.stringValue,
    checkboxCheckedIconStrokeWidth: 2.8,
    checkboxCheckedIconOutlineStrokeWidth: 2.8,
    checkboxCheckedShadow: "0 1px 2px rgba(15, 23, 42, 0.08), 0 4px 10px rgba(0, 101, 178, 0.20)",
    checkboxFocusShadow: "0 0 0 3px rgba(0, 101, 178, 0.16)",
    checkboxFocusBorderColor: RHButtons.primary.backgroundColor.stringValue,
    checkboxSuccessCheckedBackgroundColor: RHButtons.success.backgroundColor.stringValue,
    checkboxSuccessCheckedBorderColor: RHButtons.success.backgroundColor.stringValue,
    checkboxSuccessCheckedShadow: "0 1px 2px rgba(15, 23, 42, 0.08), 0 4px 10px rgba(16, 185, 129, 0.24)",
    checkboxSuccessFocusShadow: "0 0 0 3px rgba(16, 185, 129, 0.16)",
    checkboxSuccessFocusBorderColor: RHButtons.success.backgroundColor.stringValue,
    checkboxHoverBackgroundColor: RHColors.surfaceCool.stringValue,
    checkboxHoverBorderColor: RHColors.slateLight.stringValue,
    checkboxHoverShadow: "0 3px 8px rgba(15, 23, 42, 0.12)",
    checkboxActiveFilter: "brightness(0.98)",
    checkboxDisabledOpacity: "0.4",  // RHCheckbox disabled opacity

    // radio
    radioContainerPadding: "2px 0",
    radioLabelColor: RHColors.textHeading,
    radioLabelFontSize: RHFont.sizeMedium,
    radioLabelFontWeight: RHFont.weightRegular,
    radioLabelMarginLeft: "10px",
    radioControlSize: 18,
    radioControlLeadingInset: 10,
    radioControlBorderRadius: "50%",
    radioControlBackgroundColor: RHColors.white.stringValue,
    radioControlBackdropFilter: "none",
    radioControlBorderWidth: "2px",
    radioControlBorderColor: RHColors.slateLightest.stringValue,
    radioControlShadow: "0 1px 2px rgba(15, 23, 42, 0.07), 0 3px 8px rgba(15, 23, 42, 0.06)",
    radioControlTransition: "background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease",
    radioCheckedBackgroundColor: RHColors.white.stringValue,
    radioCheckedBorderColor: RHColors.primaryBlue.stringValue,
    radioCheckedBackgroundImage: `radial-gradient(circle at center, ${RHColors.primaryBlue.stringValue} 0, ${RHColors.primaryBlue.stringValue} 32%, transparent 36%)`,
    radioCheckedShadow: "0 1px 2px rgba(15, 23, 42, 0.08), 0 3px 8px rgba(0, 101, 178, 0.12), 0 0 0 4px rgba(0, 101, 178, 0.10)",
    radioFocusBorderColor: RHColors.primaryBlue.stringValue,
    radioFocusShadow: "0 0 0 4px rgba(0, 101, 178, 0.16)",
    radioHoverBackgroundColor: RHColors.surfaceCool.stringValue,
    radioHoverBorderColor: RHColors.slateLight.stringValue,
    radioHoverShadow: "0 3px 8px rgba(15, 23, 42, 0.12)",
    radioActiveFilter: "brightness(0.98)",
    get radioDisabledOpacity() { return RHForms.checkboxDisabledOpacity },  // RHRadioButton disabled opacity should stay

    // datePicker
    datePickerBorderColor: RHColors.borderField,
    get datePickerBorderRadius() { return RHForms.textFieldBorderRadius },
    get datePickerTransition() { return RHForms.textFieldTransition },
    datePickerBackgroundColor: RHColors.white,
    datePickerTextColor: RHColors.black,
    datePickerPlaceholderTextColor: RHColors.textMuted,
    datePickerDisabledTextColor: RHColors.textMuted,
    datePickerDisabledBackgroundColor: RHColors.surfaceDisabled,
    datePickerConfirmButtonBackground: RHButtons.primary.backgroundColor.stringValue,
    datePickerConfirmButtonBackgroundHover: RHButtons.primary.backgroundHoverColor.stringValue,
    datePickerConfirmButtonBackgroundActive: RHButtons.primary.backgroundActiveColor.stringValue,  // RHDatePicker OK

    // dropdown
    dropdownLabelTextColor: RHColors.textPrimary,
    dropdownLabelFontSize: "1.0625rem",
    dropdownLabelFontWeight: RHFont.weightMedium,
    dropdownLabelLineHeight: "1.25",
    dropdownLabelHeight: 24,
    dropdownLabelControlGap: RHSpacing.tiny + 2,
    dropdownControlHeight: 38,
    dropdownControlPadding: "0.3125rem 0.625rem",
    dropdownControlFontSize: RHFont.sizeSmall,
    dropdownControlLineHeight: "1.35",
    dropdownControlBorderRadius: `${RHRadius.medium}px`,
    dropdownControlTransition: "border-color .15s ease-in-out,box-shadow .15s ease-in-out",
    dropdownExpandedContainerHeight: 350,
    dropdownMenuBorderRadius: `${RHRadius.medium}px`,
    dropdownMenuBorderWidth: "1px",
    dropdownMenuBoxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
    dropdownMenuPadding: `${RHSpacing.small}px 0`,
    dropdownMenuTopMargin: `${RHSpacing.tiny}px`,
    dropdownSearchFieldBorderWidth: "1px",
    dropdownSearchFieldBorderRadius: `${RHRadius.medium}px`,
    dropdownSearchFieldMargin: `${RHSpacing.small}px 0 ${RHSpacing.tiny + 2}px ${RHSpacing.small}px`,
    dropdownSearchFieldHeight: 38,
    dropdownSearchButtonWidth: 38,
    dropdownSearchButtonBackgroundColor: RHColors.surfaceCool,
    dropdownSearchButtonIconFontSize: "18px",
    dropdownSearchButtonIconLineHeight: "1",
    dropdownSearchInputPadding: `0 ${RHSpacing.small + RHSpacing.tiny}px`,
    dropdownSearchInputFontSize: RHFont.sizeSmall,
    dropdownSearchInputLineHeight: "1.35",
    dropdownRowFontSize: RHFont.sizeSmall,
    dropdownRowLineHeight: "1.35",

    // dropdownRow
    dropdownRowTitleMarginLeftWithSelectionControl: `${RHSpacing.small + RHSpacing.tiny}px`,
    dropdownRowBorderBottomWidth: "1px",
    dropdownRowSectionBorderColor: RHColors.blackAlpha30,
    dropdownRowSelectedSectionBorderColor: RHColors.grayLegacy,
    dropdownRowSelectedHoverBackgroundColor: RHColors.surfaceNearWhite,

    // fileUpload
    fileUploadBackgroundColor: RHColors.surfaceMuted,
    fileUploadBorderRadius: `${RHRadius.large}px`,
    fileUploadBorderColor: RHColors.borderLight,
    fileUploadBoxShadow: RHShadow.subtle,
    fileUploadStatusFontSize: 12,
    fileUploadStatusTextColor: RHColors.textMuted,
    fileUploadErrorTextColor: RHColors.dangerRed,
    fileUploadFullDropZoneHeight: 128,
    fileUploadCompactDropZoneMinHeight: 48,
    fileUploadBulkDownloadButtonHeight: 40,
    fileUploadBulkDownloadButtonContentPadding: 24,
    fileUploadBulkDownloadButtonIconSize: 22,
    fileUploadBulkDownloadButtonTitleGap: 10,
    fileUploadDropZoneLabelFontSize: 13,
    fileUploadDropZoneLabelFontWeight: RHFont.weightMedium,
    fileUploadDropZoneBorderRadius: `${RHRadius.large}px`,
    fileUploadDropZoneBorderWidth: "2px",
    fileUploadDropZoneBorderStyle: "dashed",
    fileUploadDropZoneBorderColor: RHColors.borderMuted,
    fileUploadDropZoneBackgroundColor: RHColors.surfaceMuted,
    fileUploadDropZoneHoverBackgroundColor: RHColors.blueBootstrapAlpha03.stringValue,
    fileUploadDropZoneActiveBackgroundColor: RHColors.blueBootstrapAlpha05.stringValue,
    fileUploadDropZoneLabelTextColor: RHColors.grayMedium,
    fileUploadDropZoneHoverLabelTextColor: RHColors.bootstrapBlue,
    fileUploadDropZoneTransition: "all 0.2s ease",
    fileUploadDropZoneDisabledOpacity: "0.6",  // RHDropZone disabled opacity

    // fileList
    fileListPadding: 5,
    fileListFileGap: 2.5,
    fileListCompactPadding: 0,
    fileListCompactFileGap: RHSpacing.tiny,

    // fileItem
    fileItemBackgroundColor: RHColors.white,
    fileItemBackgroundTintColor: RHColors.primaryBlueSurfaceFaint,
    fileItemBorderColor: RHColors.borderBlueFaint,
    fileItemBorderRadius: `${RHRadius.medium}px`,
    fileItemBoxShadow: "0 1px 2px rgba(15, 23, 42, 0.06), 0 4px 12px rgba(0, 101, 178, 0.05)",
    fileItemMinRowHeight: 48,
    fileItemCompactBackgroundTintColor: UIColor.transparentColor,
    fileItemCompactBorderColor: UIColor.transparentColor,
    fileItemCompactBorderRadius: `${RHRadius.small}px`,
    fileItemCompactBoxShadow: "none",
    fileItemCompactMinRowHeight: 34,
    fileItemCompactActionButtonSize: 28,
    fileItemCompactActionButtonGap: RHSpacing.small,
    fileItemCompactNameGap: RHSpacing.small,
    fileItemCompactDownloadIconSvgSize: 10.5,
    fileItemCompactNameFontSize: RHFont.sizeXSmall,
    fileItemCompactNameFontWeight: RHFont.weightRegular,
    fileItemFileIconColumnWidth: 36,
    fileItemFileIconSize: 28,
    fileItemFileIconSvgSize: 16,
    fileItemFileIconBorderRadius: `${RHRadius.medium}px`,
    fileItemFileIconBackgroundColor: RHColors.primaryBlueSurfaceIcon,
    fileItemFileIconTextColor: RHButtons.primary.backgroundColor,
    fileItemFileIconBorderColor: RHColors.primaryBlueAlpha16,
    fileItemNameFontFamily: RHFont.familyBase,
    fileItemNameFontSize: RHFont.sizeSmall,
    fileItemNameFontWeight: RHFont.weightSemibold,
    fileItemNameTextColor: RHColors.textHeading,
    fileItemMetadataFontSize: RHFont.sizeXSmall,
    fileItemMetadataTextColor: RHColors.slateMedium,
    fileItemActionButtonSize: 32,
    fileItemActionButtonGap: RHSpacing.small,
    fileItemActionButtonBorderRadius: `${RHRadius.medium}px`,
    fileItemDownloadIconSvgSize: 16,
    fileItemDownloadButtonVariant: "primary" as const,
    fileItemDeleteButtonVariant: "softDanger" as const,

    // panel
    panelBackgroundColor: RHColors.surfaceMuted,
    panelBorderRadius: `${RHRadius.large}px`,
    panelBorderColor: RHColors.borderSoft,
    panelBoxShadow: RHShadow.subtle,

    // field
    fieldSingleLineFieldHeight: 40,  // procurement-form single-line inputs should stay slightly

}


// =============================================================================
//  RHTables
// =============================================================================

export const RHTables = {

    // dataView
    dataViewBackgroundColor: RHColors.white,
    dataViewBorderColor: RHColors.borderSoft,
    dataViewBorderRadius: `${RHRadius.medium}px`,
    dataViewBoxShadow: "0 1px 2px rgba(15, 23, 42, 0.05), 0 8px 20px rgba(15, 23, 42, 0.06)",
    dataViewGridFocusOutline: "none",
    dataViewGridFocusOutlineOffset: "0px",
    dataViewTitleTextColor: RHColors.textTableHeading,
    dataViewTitleFontFamily: RHFont.familyBase,
    dataViewTitleFontSize: RHFont.sizeLarge,
    dataViewTitleFontWeight: RHFont.weightMedium,
    dataViewTitleMarginBottom: "0.5rem",
    dataViewTitleLineHeight: "1.2",
    dataViewNoDataTextColor: RHColors.slateDark,
    dataViewNoDataFontSize: RHFont.sizeLarge,
    dataViewNoDataFontWeight: RHFont.weightMedium,
    dataViewNoDataLineHeight: "1.4",
    dataViewNoDataMessageHeight: 48,
    dataViewNoDataMessageTopGap: 40,
    dataViewPageHeight: 900,
    dataViewTableHeaderHeight: 56,
    dataViewMultilineHeaderHeight: 64,
    dataViewSearchFieldHeight: 40,
    dataViewSearchAccessoryTopGap: RHSpacing.small,

    // header
    headerBackgroundColor: RHColors.tableHeaderBlueSurface,
    headerTextColor: RHColors.textTableHeading,
    headerActiveTextColor: RHColors.primaryBlue,
    headerFontFamily: RHFont.familyBase,
    headerFontWeight: RHFont.weightMedium,
    headerFontSize: RHFont.sizeSmall,
    headerLetterSpacing: "0.01em",
    headerTextAlignment: UITextView.textAlignment.center,
    headerCellPadding: "0.75rem 0.625rem",
    headerMultilineLineHeight: RHFont.lineHeightHeading,
    headerMultilineSortButtonContentPadding: 2,
    headerSortIconOpacity: "0.58",
    headerBorderBottomColor: RHColors.borderHeaderTint,
    headerBorderBottomWidth: "1px",

    // row
    rowEvenBackgroundColor: RHColors.white,
    rowOddBackgroundColor: RHColors.surfaceRowOdd,
    rowHoverBackgroundColor: RHColors.tableRowHoverBlueSurface,
    rowBorderBottomColor: RHColors.borderRowFaint,
    rowBorderBottomWidth: "1px",
    rowTextColor: RHColors.textTableHeading,
    rowActionTextColor: RHColors.primaryBlue,
    rowFontFamily: RHFont.familyBase,
    rowFontSize: RHFont.sizeSmall,
    rowButtonFontWeight: RHFont.weightMedium,
    rowMinHeight: 56,
    rowDisclosureSize: 20,
    rowDisclosureContentGap: 6,
    rowDisclosureIndentStep: 15,
    rowDisclosureChevronSize: 8,
    rowDisclosureChevronStrokeWidth: 2,
    rowDisclosureFontSize: RHFont.sizeSmall,
    rowDisclosureFontWeight: RHFont.weightSemibold,
    rowDisclosureTextColor: RHColors.slateMedium,
    rowDisclosureHoverTextColor: RHColors.primaryBlue,
    rowDisclosureExpandedTextColor: RHColors.primaryBlue,
    rowDisclosureBackgroundColor: RHColors.transparent,
    rowDisclosureHoverBackgroundColor: RHColors.transparent,
    rowDisclosureExpandedBackgroundColor: RHColors.transparent,
    rowDisclosureBorderColor: RHColors.transparent,
    rowDisclosureHoverBorderColor: RHColors.transparent,
    rowDisclosureBorderRadius: "999px",
    rowDisclosureBoxShadow: "none",
    rowEmphasizedDisclosureSize: 18,
    rowEmphasizedDisclosureCollapsedIconMarkup: `<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="10" viewBox="0 0 10 10" preserveAspectRatio="xMidYMid meet" fill="none" style="display:block" aria-hidden="true"><path d="M3.25 1.5L6.75 5L3.25 8.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
    rowEmphasizedDisclosureExpandedIconMarkup: `<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="10" viewBox="0 0 10 10" preserveAspectRatio="xMidYMid meet" fill="none" style="display:block" aria-hidden="true"><path d="M1.5 3.25L5 6.75L8.5 3.25" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
    rowEmphasizedDisclosureTextColor: RHColors.white,
    rowEmphasizedDisclosureBackgroundColor: RHColors.primaryBlue,
    rowEmphasizedDisclosureExpandedBackgroundColor: RHColors.primaryBlue,
    rowEmphasizedDisclosureBorderColor: RHColors.primaryBlueActive,
    rowEmphasizedDisclosureExpandedBorderColor: RHColors.primaryBlueActive,
    rowEmphasizedDisclosureBorderRadius: `${RHRadius.small}px`,
    rowEmphasizedDisclosureBoxShadow: `0 1px 2px ${RHColors.primaryBlueAlpha22.stringValue}`,
    rowEmphasizedDisclosureTransition: "background-color 0.14s ease, border-color 0.14s ease, color 0.14s ease",
    rowRowChromeHorizontalInset: 0,  // RHTableRowView layout: keep row chrome flush with header

    // tree
    treeIndentStep: 28,
    treeExpanderSize: 24,
    treeExpanderLeadingInset: 0,
    treeExpanderReservedColumnWidth: 24,
    treeExpanderToContentGap: 10,
    treeExpanderFontSize: RHFont.sizeLarge,
    treeExpanderFontWeight: RHFont.weightExtraBold,
    treeExpanderBorderRadius: `${RHRadius.small}px`,
    treeExpanderCollapsedBackgroundColor: RHColors.whiteAlpha98,
    treeExpanderCollapsedBorderColor: RHColors.primaryBlueAlpha22,
    treeExpanderExpandedBackgroundColor: RHColors.blueGlass,
    treeExpanderExpandedBorderColor: RHColors.primaryBlueAlpha42,
    treeExpanderTextColor: RHColors.primaryBlue,
    treeExpanderShadow: "0 1px 2px rgba(0, 101, 178, 0.08)",
    treeExpanderTransition: "background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, color 0.18s ease",
    treeConnectorColor: RHColors.slateAlpha92,
    treeConnectorHorizontalLength: 14,
    treeConnectorOffsetFromExpander: 8,  // connector stem should sit just left of the

}


// =============================================================================
//  RHBadges
// =============================================================================

export const RHBadges = {

    // notification
    notificationMaxVisibleCount: 99,
    notificationMarginLeft: 6,
    notificationInlineHeight: 18,
    notificationInlineMinWidth: 18,
    notificationInlinePaddingX: 6,
    notificationInlineBorderRadius: "999px",
    notificationInlineFontSize: RHFont.sizeXSmall,
    notificationInlineFontWeight: RHFont.weightBold,
    notificationInlineLineHeight: "18px",
    notificationInlineShadow: "0 2px 6px rgba(220, 38, 68, 0.22)",
    notificationInlineVerticalTransform: "translateY(-1px)",
    notificationInfoCountMarginLeft: 4,
    notificationInfoCountFontSize: RHFont.sizeXSmall,
    notificationInfoCountFontWeight: RHFont.weightSemibold,
    notificationInfoCountLineHeight: "1",
    notificationInfoCountOpacity: "0.78",
    notificationInfoCountVerticalTransform: "translateY(-1px)",
    notificationNavBackgroundColor: RHColors.dangerBadgePink,
    notificationNavTextColor: RHColors.white,
    notificationNavBorderColor: RHColors.whiteAlpha50,
    notificationActionBackgroundColor: RHColors.white,
    notificationActionTextColor: RHColors.dangerBadgeText,
    notificationActionBorderColor: RHColors.whiteAlpha75,
    notificationActionShadow: "0 1px 4px rgba(0, 0, 0, 0.18)",
    notificationTabBackgroundColor: RHColors.dangerBadgePink,
    notificationTabTextColor: RHColors.white,
    notificationTabBorderColor: RHColors.badgePinkBorderAlpha25,
    notificationTaskLabel: "Tegutse",
    notificationTaskLabels: {
        evaluateOffers: "Hinda",
        answerQuestions: "Vasta",
        reviewOfferResult: "Tulemus",
        joinRequests: "Taotlus",
        multipleProcurementActions: "2",
        newOffer: "Uus",                  // procurer: an unseen submitted offer
        answeredQuestion: "Vastus"        // offerer: an unseen answer to their question
    },
    notificationTaskBackgroundColor: RHColors.warningOrange,
    notificationTaskTextColor: RHColors.white,
    notificationTaskBorderColor: RHColors.warningOrangeDark,
    notificationTaskShadow: "0 2px 6px rgba(249, 115, 22, 0.26)",
    notificationTaskCountSize: 18,
    notificationTaskCountBackgroundColor: RHColors.warningOrange,
    notificationTaskCountTextColor: RHColors.white,
    notificationTaskCountBorderColor: RHColors.warningOrangeDark,
    notificationTaskCountShadow: "0 2px 6px rgba(249, 115, 22, 0.26)",
    notificationDotSize: 9,
    notificationDotMarginLeft: 6,
    notificationDotBackgroundColor: RHColors.warningOrange,
    notificationDotBorderColor: RHColors.warningOrangeDark,
    notificationDotShadow: "0 2px 6px rgba(249, 115, 22, 0.26)",
    notificationPageBackgroundColor: RHColors.dangerBadgePink,
    notificationPageTextColor: RHColors.white,
    notificationPageHeight: 24,
    notificationPagePaddingX: 10,
    notificationPageFontSize: RHFont.sizeXSmall,
    notificationPageFontWeight: RHFont.weightBold,
    notificationPageShadow: "0 3px 8px rgba(220, 38, 68, 0.22)",

    // tooltip
    tooltipMaxWidth: 480,
    tooltipBackgroundColor: RHColors.tooltipSurface,
    tooltipBorderRadius: `${RHRadius.medium}px`,
    tooltipBoxShadow: "0 4px 16px rgba(0,0,0,0.28), 0 1px 4px rgba(0,0,0,0.18)",
    tooltipBorder: "1px solid rgba(255,255,255,0.08)",
    tooltipTextColor: RHColors.tooltipText.stringValue,
    tooltipFontSize: "14px",
    tooltipFontWeight: RHFont.weightRegular,
    tooltipLineHeight: "1.6",
    tooltipWhiteSpace: "pre-wrap",
    tooltipWordBreak: "normal",
    tooltipOverflowWrap: "break-word",
    tooltipLabelWidth: "max-content",

    // filterChip (kept nested because it is consumed as a whole object)
    filterChip: {

    // Geometry
    rowHeight: 32,
    // Includes label, separator, and count text so chips hug their content.
    horizontalPadding: 26,
    borderRadius: `${RHRadius.medium}px`,
    borderWidth: "1px",
    fontSize: "13px",
    fontWeight: RHFont.weightMedium,
    columnGap: RHSpacing.small,
    rowGap: RHSpacing.small,

    // Larger standalone chip variant.
    large: {
        rowHeight: 38,
        fontSize: "14px",
        horizontalPadding: 32
    },

    // Inactive
    inactiveTextColor: RHColors.slateDarkest,
    inactiveBackgroundColor: UIColor.whiteColor,
    inactiveHoverBackgroundColor: RHColors.surfaceMuted,
    inactivePressedBackgroundColor: RHColors.surfaceSubtle,
    inactiveBorderColor: RHColors.borderLight,
    inactiveHoverBorderColor: RHColors.grayLight,
    inactiveShadow: "none",
    inactiveHoverShadow: "0 1px 2px rgba(15, 23, 42, 0.08)",

    // Active
    activeTextColor: RHColors.successGreenChipText,
    activeBackgroundColor: RHColors.successGreenSurfaceLight,
    activeHoverBackgroundColor: RHColors.successGreenSurfaceHover,
    activePressedBackgroundColor: RHColors.successGreenSurfacePressed,
    activeBorderColor: RHColors.successGreen,
    activeHoverBorderColor: RHColors.successGreenDark,
    activeShadow: "none",
    activeHoverShadow: "none",

    // Soft open state
    softTextColor: RHColors.slateDarkest,
    softBackgroundColor: RHColors.surfaceSubtle,
    softHoverBackgroundColor: RHColors.surfaceDisabled,
    softBorderColor: RHColors.grayLight,
    softShadow: "none",

    // Focus
    focusOutline: "3px solid rgba(16, 185, 129, 0.35)",
    focusOutlineOffset: "1px",

    // Inline icons inherit the chip text color through currentColor.
    funnelIconMarkup: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="currentColor" style="vertical-align:-2px;margin-right:6px" aria-hidden="true"><path d="M1.5 1.5A.5.5 0 0 1 2 1h12a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.128.334L10 8.692V13.5a.5.5 0 0 1-.342.474l-3 1A.5.5 0 0 1 6 14.5V8.692L1.628 3.834A.5.5 0 0 1 1.5 3.5z"/></svg>`,
    caretCollapsedMarkup: `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 16 16" fill="currentColor" style="vertical-align:-1px;margin-left:6px" aria-hidden="true"><path d="M3.204 5h9.592L8 10.481zm-.753.659 4.796 5.48a1 1 0 0 0 1.506 0l4.796-5.48c.566-.647.106-1.659-.753-1.659H3.204a1 1 0 0 0-.753 1.659"/></svg>`,
    caretExpandedMarkup: `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 16 16" fill="currentColor" style="vertical-align:-1px;margin-left:6px" aria-hidden="true"><path d="M7.247 4.86l-4.796 5.481c-.566.647-.106 1.659.753 1.659h9.592a1 1 0 0 0 .753-1.659l-4.796-5.48a1 1 0 0 0-1.506 0z"/></svg>`

},

    // alertBanner
    alertBannerOutcomeFontWeight: RHFont.weightBold,
    alertBannerPointerSize: 12,
    alertBannerPointerEdgeInset: 12,
    alertBannerPointerBorderInset: 1,
    alertBannerGlassInset: 0,
    alertBannerGradientBorderWidth: 2,
    alertBannerGlassOverlayBackground: RHColors.transparent.stringValue,
    alertBannerGlassOverlayBoxShadow: "none",
    alertBannerQuestionLightTealBlueBubbleBackground: RHColors.tealMintSurface,
    alertBannerQuestionLightTealBlueBubbleBackgroundGradient: `linear-gradient(135deg, ${RHColors.tealMintSurface.stringValue} 0%, #c9f1e3 30%, #d7eef8 68%, #c8e3f2 100%)`,
    alertBannerQuestionLightTealBlueBubbleBorderGradient: "linear-gradient(135deg, #22c1b8 0%, #2f9de8 58%, #8b5cf6 100%)",
    alertBannerQuestionLightTealBlueBubblePointerBackground: RHColors.tealMintSurface,
    alertBannerQuestionLightTealBlueBubbleBorderColor: RHColors.primaryBlueAlpha18,
    alertBannerQuestionLightTealBlueBubbleTextColor: RHColors.textHeading,
    alertBannerQuestionTealBlueIndigoBubbleBackground: RHColors.infoBlueSurface,
    alertBannerQuestionTealBlueIndigoBubbleBackgroundGradient: "linear-gradient(135deg, #4ABFAA 0%, #1EA7D6 40%, #2B7CC5 70%, #3A3AAA 100%)",
    alertBannerQuestionTealBlueIndigoBubbleBorderGradient: "linear-gradient(135deg, #22c1b8 0%, #2f9de8 58%, #8b5cf6 100%)",
    alertBannerQuestionTealBlueIndigoBubblePointerBackground: RHColors.infoBlueSurface,
    alertBannerQuestionTealBlueIndigoBubbleBorderColor: RHColors.primaryBlueAlpha36,
    alertBannerQuestionTealBlueIndigoBubbleTextColor: RHColors.textHeading,
    alertBannerQuestionSteelSlateNavyBubbleBackground: RHColors.borderSlate,
    alertBannerQuestionSteelSlateNavyBubbleBackgroundGradient: "linear-gradient(135deg, #5588BB 0%, #5577AA 40%, #334488 70%, #1A2255 100%)",
    alertBannerQuestionSteelSlateNavyBubbleBorderGradient: `linear-gradient(135deg, ${RHColors.slateMedium.stringValue} 0%, #2f9de8 58%, #8b5cf6 100%)`,
    alertBannerQuestionSteelSlateNavyBubblePointerBackground: RHColors.borderSlate,
    alertBannerQuestionSteelSlateNavyBubbleBorderColor: RHColors.navyAlpha40,
    alertBannerQuestionSteelSlateNavyBubbleTextColor: RHColors.textHeading,
    alertBannerQuestionRedCrimsonMaroonBubbleBackground: RHColors.dangerPinkSurface,
    alertBannerQuestionRedCrimsonMaroonBubbleBackgroundGradient: "linear-gradient(135deg, #BB3333 0%, #993322 40%, #771122 70%, #440A11 100%)",
    alertBannerQuestionRedCrimsonMaroonBubbleBorderGradient: "linear-gradient(135deg, #d65353 0%, #e15f85 55%, #8b5cf6 100%)",
    alertBannerQuestionRedCrimsonMaroonBubblePointerBackground: RHColors.dangerPinkSurface,
    alertBannerQuestionRedCrimsonMaroonBubbleBorderColor: RHColors.maroonAlpha40,
    alertBannerQuestionRedCrimsonMaroonBubbleTextColor: RHColors.dangerMaroonText,
    alertBannerQuestionTealGreenBubbleBackground: RHColors.successGreenSurface,
    alertBannerQuestionTealGreenBubbleBackgroundGradient: `linear-gradient(135deg, ${RHColors.successGreenSurface.stringValue} 0%, ${RHColors.successGreenSurfaceHover.stringValue} 100%)`,
    alertBannerQuestionTealGreenBubbleBorderGradient: "linear-gradient(135deg, #34d399 0%, #22c55e 100%)",
    alertBannerQuestionTealGreenBubblePointerBackground: RHColors.successGreenSurface,
    alertBannerQuestionTealGreenBubbleBorderColor: RHColors.greenBorderAlpha34,
    alertBannerQuestionTealGreenBubbleTextColor: RHColors.successGreenTextDark,
    alertBannerAnsweredQuestionBubbleBackground: RHColors.successGreenSurface,
    alertBannerAnsweredQuestionBubbleBackgroundGradient: `linear-gradient(135deg, ${RHColors.successGreenSurface.stringValue} 0%, ${RHColors.successGreenSurfaceHover.stringValue} 100%)`,
    alertBannerAnsweredQuestionBubbleBorderGradient: "linear-gradient(135deg, #34d399 0%, #22c55e 100%)",
    alertBannerAnsweredQuestionBubblePointerBackground: RHColors.successGreenSurface,
    alertBannerAnsweredQuestionBubbleBorderColor: RHColors.greenBorderAlpha34,
    alertBannerAnsweredQuestionBubbleTextColor: RHColors.black,
    alertBannerUnansweredQuestionBubbleBackground: RHColors.dangerPinkSurface,
    alertBannerUnansweredQuestionBubbleBackgroundGradient: `linear-gradient(135deg, #fff1f2 0%, ${RHColors.dangerPinkSurface.stringValue} 48%, #fecdd3 100%)`,
    alertBannerUnansweredQuestionBubbleBorderGradient: "linear-gradient(135deg, #fb7185 0%, #e11d48 100%)",
    alertBannerUnansweredQuestionBubblePointerBackground: RHColors.dangerPinkSurface,
    alertBannerUnansweredQuestionBubbleBorderColor: RHColors.softDangerBorderAlpha26,
    alertBannerUnansweredQuestionBubbleTextColor: RHColors.black,
    alertBannerProcurerBroadcastBubbleBackground: RHColors.primaryBlueSurface,
    alertBannerProcurerBroadcastBubbleBackgroundGradient: `linear-gradient(135deg, ${RHColors.primaryBlueSurface.stringValue} 0%, #cfe2f3 100%)`,
    alertBannerProcurerBroadcastBubbleBorderGradient: `linear-gradient(135deg, #3b8fd4 0%, ${RHColors.primaryBlue.stringValue} 100%)`,
    alertBannerProcurerBroadcastBubblePointerBackground: RHColors.primaryBlueSurface,
    alertBannerProcurerBroadcastBubbleBorderColor: RHColors.primaryBlueAlpha30,
    alertBannerProcurerBroadcastBubbleTextColor: RHColors.black,
    alertBannerWarningBubbleBackground: RHColors.warningAmberSurfaceDraft,
    alertBannerWarningBubbleBackgroundGradient: `linear-gradient(135deg, ${RHColors.warningAmberSurfaceDraft.stringValue} 0%, ${RHColors.warningAmberSurface.stringValue} 100%)`,
    alertBannerWarningBubbleBorderGradient: `linear-gradient(135deg, ${RHColors.warningAmberBorderDraft.stringValue} 0%, ${RHColors.warningAmberBorder.stringValue} 100%)`,
    alertBannerWarningBubblePointerBackground: RHColors.warningAmberSurfaceDraft,
    alertBannerWarningBubbleBorderColor: RHColors.warningAmberBorderDraft,
    alertBannerWarningBubbleTextColor: RHColors.warningAmberTextDeep,
    alertBannerSuccessBubbleBackground: RHColors.successGreenSurfaceFaint,
    alertBannerSuccessBubbleBackgroundGradient: `linear-gradient(135deg, ${RHColors.successGreenSurfaceFaint.stringValue} 0%, ${RHColors.successGreenSurfaceLight.stringValue} 100%)`,
    alertBannerSuccessBubbleBorderGradient: "linear-gradient(135deg, rgba(16, 185, 129, 0.34) 0%, rgba(5, 150, 105, 0.50) 100%)",
    alertBannerSuccessBubblePointerBackground: RHColors.successGreenSurfaceLight,
    alertBannerSuccessBubbleBorderColor: RHColors.successGreenAlpha42,
    alertBannerSuccessBubbleTextColor: RHColors.successGreenTextDeep,

}


// =============================================================================
//  RHDialogs
// =============================================================================

export const RHDialogs = {

    // shared dialog surface
    dialogSurfaceBackgroundColor: UIColor.whiteColor,
    dialogSurfaceBorderRadius: `${RHRadius.large}px`,
    dialogSurfaceBoxShadow: RHShadow.strong,
    dialogContentInset: RHSpacing.large,
    get dialogTitleTextViewType() { return UITextView.type.header4 },
    get dialogTitleTextColor() { return RHColors.textPrimary },
    loadingIndicatorColor: UIColor.whiteColor,
    loadingIndicatorDialogContentHeight: 160,
    loadingIndicatorDialogContentWidth: 250 - RHSpacing.large * 2,
    loadingIndicatorSize: 50,

    // confirmation
    confirmationTitleTextColor: RHColors.textPrimary,
    confirmationTitleFontSize: RHFont.sizeMedium,
    confirmationTitleFontWeight: RHFont.weightRegular,
    confirmationTitleLineHeight: RHFont.lineHeightHeading,
    confirmationAlertTextColor: RHColors.textPrimary,
    confirmationAlertTextAlignment: UITextView.textAlignment.center,
    confirmationAlertTextFontSize: RHFont.sizeMedium,
    confirmationAlertTextFontWeight: RHFont.weightRegular,
    confirmationAlertTextLineHeight: RHFont.lineHeightHeading,
    confirmationAlertButtonTitle: "OK",
    confirmationAlertButtonVariant: "primary" as const,
    confirmationAlertButtonWidth: 120,
    confirmationAlertButtonHeight: RHButtons.compactHeight,
    confirmationAlertButtonContentPadding: RHButtons.compactContentPadding,
    confirmationAlertButtonFontSize: RHButtons.compactFontSize,
    confirmationAlertDialogMinHeight: 200,
    confirmationQuestionTitle: "Olete kindel?",
    confirmationQuestionTextColor: RHColors.textPrimary,
    confirmationQuestionFontSize: RHFont.sizeXLarge,
    confirmationQuestionFontWeight: RHFont.weightRegular,
    confirmationQuestionLineHeight: RHFont.lineHeightHeading,
    confirmationQuestionTopGap: RHSpacing.small,
    confirmationQuestionHeadingTextColor: RHColors.textPrimary,
    confirmationQuestionHeadingTextAlignment: UITextView.textAlignment.center,
    confirmationQuestionHeadingFontSize: RHFont.sizeLarge,
    confirmationQuestionHeadingFontWeight: RHFont.weightRegular,
    confirmationQuestionHeadingLineHeight: RHFont.lineHeightHeading,
    confirmationQuestionSupportingTextColor: RHColors.textMuted,
    confirmationQuestionSupportingTextAlignment: UITextView.textAlignment.center,
    confirmationQuestionSupportingTextFontSize: RHFont.sizeMedium,
    confirmationQuestionSupportingTextFontWeight: RHFont.weightRegular,
    confirmationQuestionSupportingTextLineHeight: RHFont.lineHeightBase,
    confirmationQuestionHeadingToSupportingTextGap: RHSpacing.tiny,
    confirmationDialogContentInset: RHSpacing.xLarge,
    confirmationQuestionDialogPreferredWidth: 600,
    confirmationCloseButtonSize: 24,
    confirmationCloseButtonTextGap: RHSpacing.small,
    confirmationButtonTopGap: RHSpacing.large,
    confirmationButtonWidth: 120,
    confirmationButtonHeight: RHButtons.compactHeight,
    confirmationButtonGap: RHSpacing.medium,
    confirmationButtonContentPadding: RHButtons.compactContentPadding,
    confirmationButtonFontSize: RHButtons.compactFontSize,
    confirmationYesButtonTitle: "JAH",
    confirmationYesButtonVariant: "success" as const,
    confirmationNoButtonTitle: "EI",
    confirmationNoButtonVariant: RHButtons.destructiveUtilityVariant,

    // cpvTree
    cpvTreeDialogMaxWidth: 1440,
    cpvTreeDialogWidthViewportRatio: 0.96,
    cpvTreeDialogHeightViewportRatio: 0.94,
    cpvTreeTreeViewHeight: 760,
    cpvTreeMinimumTreeViewHeight: 360,
    cpvTreeTableHeaderFontSize: RHFont.sizeMedium,
    cpvTreeTableHeaderFontWeight: RHFont.weightBold,
    cpvTreeCheckboxSelectionAccent: "success" as const,
    cpvTreeSelectedSummaryHeight: RHButtons.compactHeight + 2,
    cpvTreeSelectedSummaryBorderRadius: `${RHRadius.medium}px`,
    cpvTreeSelectedSummaryHorizontalInset: RHSpacing.medium,
    cpvTreeSelectedSummaryFontSize: RHFont.sizeMedium,
    cpvTreeSelectedSummarySelectedTextColor: RHColors.successGreenTextDeep,
    cpvTreeSelectedSummarySelectedBackgroundColor: RHColors.greenGlass,
    cpvTreeSelectedSummarySelectedBorderColor: RHColors.successGreenAlpha34,
    cpvTreeSelectedSummarySelectedShadow: "0 1px 3px rgba(16, 185, 129, 0.10)",
    cpvTreeSelectedSummaryUnselectedTextColor: RHColors.slateMedium,
    cpvTreeSelectedSummaryUnselectedBackgroundColor: RHColors.slateGlass,
    cpvTreeSelectedSummaryUnselectedBorderColor: RHColors.slateAlpha95,
    cpvTreeSelectedSummaryUnselectedShadow: "0 1px 2px rgba(15, 23, 42, 0.03)",
    cpvTreeCancelButtonVariant: RHButtons.destructiveUtilityVariant,
    cpvTreeConfirmButtonVariant: "success" as const,
    cpvTreeFooterButtonHeight: RHButtons.compactPrimaryHeight,
    cpvTreeFooterButtonGap: RHSpacing.medium,
    get cpvTreeFooterButtonContentPadding() { return RHButtons.compactPrimaryContentPadding },
    get cpvTreeFooterButtonFontSize() { return RHButtons.compactPrimaryFontSize },
    cpvTreeFooterButtonTextSafetyBuffer: RHSpacing.large,
    cpvTreeFooterButtonMinWidth: 220,
    cpvTreeFooterButtonMaxWidth: 280,
    cpvTreeFooterBottomPadding: RHSpacing.xLarge,  // keep a touch more air below the centered footer

    // share (kept nested because it is consumed as a whole object)
    share: {

    // Share button
    buttonVariant: "light" as const,
    buttonHeight: 30,
    buttonWidth: 92,
    buttonContentPadding: RHSpacing.small,
    buttonFontSize: RHFont.sizeSmall,
    buttonTopRightInset: RHSpacing.tiny,
    buttonToContentGap: RHSpacing.small,
    // Inline share glyph rendered through currentColor.
    iconMarkup: `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 16 16" fill="currentColor" style="vertical-align:-2px;margin-right:6px" aria-hidden="true"><path d="M11 2.5a2.5 2.5 0 1 1 .603 1.628l-6.718 3.12a2.5 2.5 0 0 1 0 1.504l6.718 3.12a2.5 2.5 0 1 1-.488.876l-6.718-3.12a2.5 2.5 0 1 1 0-3.256l6.718-3.12A2.5 2.5 0 0 1 11 2.5"/></svg>`,

    // Share dialog
    dialogMaxWidth: 540,
    dialogWidthViewportRatio: 0.94,
    dialogViewportMargin: RHSpacing.xLarge,
    dialogBackgroundColor: UIColor.whiteColor,
    dialogBorderRadius: `${RHRadius.large}px`,
    dialogBoxShadow: RHShadow.strong,
    dialogContentInset: RHSpacing.large,
    get dialogTitleTextViewType() { return UITextView.type.header4 },
    get dialogTitleTextColor() { return RHColors.textPrimary },
    dialogTitle: "Jaga hanget",
    sectionGap: RHSpacing.medium,
    sectionLabelFontSize: RHFont.sizeSmall,
    sectionLabelTextColor: RHColors.slateDarkest,
    rowHeight: 40,
    linkFieldTextColor: RHColors.slateDarkest,
    dividerColor: RHColors.borderLight,
    copyButtonVariant: "primary" as const,
    copyButtonWidth: 132,
    copyButtonContentPadding: RHSpacing.medium,
    copyButtonFontSize: RHFont.sizeSmall,
    copyButtonTitle: "Kopeeri link",
    copyButtonCopiedTitle: "Link kopeeritud!",
    sendButtonVariant: "success" as const,
    sendButtonWidth: 110,
    sendButtonContentPadding: RHSpacing.medium,
    sendButtonFontSize: RHFont.sizeSmall,
    sendButtonTitle: "Saada",
    sendButtonSendingTitle: "Saadan...",
    closeButtonVariant: "softDanger" as const,
    closeButtonWidth: 120,
    closeButtonTitle: "Sulge",
    footerButtonGap: RHSpacing.medium,
    sendSectionLabel: "Saada aadressaatidele",

    // E-mail recipient chip input
    inputBackgroundColor: UIColor.whiteColor,
    inputBorderColor: RHColors.grayLight,
    inputBorderRadius: `${RHRadius.medium}px`,
    inputContainerInset: RHSpacing.small,
    inputRowHeight: 28,
    inputMinHeight: 120,
    inputItemGap: RHSpacing.tiny,
    inputPlaceholder: "Lisa e-posti aadress ja vajuta Enter",
    chipBackgroundColor: RHColors.indigoSurface,
    chipBorderColor: RHColors.indigoBorder,
    chipTextColor: RHColors.indigoText,
    chipBorderRadius: `${RHRadius.xLarge}px`,
    chipFontSize: RHFont.sizeSmall,
    chipHorizontalPadding: RHSpacing.small,
    chipLabelToRemoveGap: RHSpacing.tiny,
    chipRemoveButtonSize: 16,
    chipRemoveTextColor: RHColors.indigoStrong,
    chipRemoveGlyph: "\u00D7"

},

    // askQuestion
    askQuestionDialogMaxWidth: 920,
    askQuestionDialogWidthViewportRatio: 0.9,
    askQuestionDialogBorderRadius: `${RHRadius.large}px`,
    askQuestionDialogBackgroundColor: UIColor.whiteColor,
    askQuestionDialogBoxShadow: RHShadow.strong,
    askQuestionContentInset: RHSpacing.large,
    get askQuestionTitleTextViewType() { return UITextView.type.header4 },
    get askQuestionTitleTextColor() { return RHColors.textPrimary },
    askQuestionTitleFontFamily: RHFont.familyBase,
    askQuestionTitleFontSize: RHFont.sizeXXLarge,
    askQuestionTitleFontWeight: RHFont.weightMedium,
    askQuestionTitleLineHeight: RHFont.lineHeightHeading,
    askQuestionExplanationTextColor: RHColors.textPrimary,
    askQuestionExplanationFontFamily: RHFont.familyBase,
    askQuestionExplanationFontSize: RHFont.sizeMedium,
    askQuestionExplanationFontWeight: RHFont.weightRegular,
    askQuestionExplanationLineHeight: RHFont.lineHeightHeading,
    askQuestionTitleBottomGap: RHSpacing.medium,
    askQuestionExplanationBottomGap: RHSpacing.medium,
    askQuestionAccordionBottomGap: RHSpacing.medium,
    askQuestionTextAreaHeight: 200,
    askQuestionFooterButtonsTopGap: RHSpacing.large,
    askQuestionFooterButtonHeight: RHButtons.compactHeight,
    askQuestionFooterButtonGap: RHSpacing.medium,
    askQuestionFooterButtonContentPadding: RHButtons.compactContentPadding,
    askQuestionFooterButtonFontSize: RHButtons.compactFontSize,
    askQuestionFooterButtonTextSafetyBuffer: RHSpacing.medium,
    askQuestionFooterButtonMinWidth: 180,
    askQuestionFooterButtonMaxWidth: 260,
    askQuestionFooterScrollBottomPadding: RHSpacing.large,
    askQuestionSubmitButtonVariant: "success" as const,
    askQuestionCancelButtonVariant: RHButtons.destructiveUtilityVariant,  // modal Tühista uses the shared soft-danger language

    // evaluationNotes
    evaluationNotesDialogMaxWidth: 620,
    evaluationNotesDialogWidthViewportRatio: 0.9,
    evaluationNotesDialogBorderRadius: `${RHRadius.large}px`,
    evaluationNotesDialogBackgroundColor: UIColor.whiteColor,
    evaluationNotesDialogBoxShadow: RHShadow.strong,
    evaluationNotesContentInset: RHSpacing.large,
    evaluationNotesTitleText: "Tulemi põhjendus",
    get evaluationNotesTitleTextViewType() { return UITextView.type.header4 },
    get evaluationNotesTitleTextColor() { return RHColors.textPrimary },
    evaluationNotesTitleFontFamily: RHFont.familyBase,
    evaluationNotesTitleFontSize: RHFont.sizeXXLarge,
    evaluationNotesTitleFontWeight: RHFont.weightMedium,
    evaluationNotesTitleLineHeight: RHFont.lineHeightHeading,
    evaluationNotesTitleBottomGap: RHSpacing.medium,
    evaluationNotesTextAreaHeight: 180,
    evaluationNotesTextAreaPlaceholder: "Sisesta põhjendus",
    evaluationNotesFooterButtonsTopGap: RHSpacing.large,
    evaluationNotesFooterButtonHeight: RHButtons.compactHeight,
    evaluationNotesFooterButtonGap: RHSpacing.medium,
    evaluationNotesFooterButtonContentPadding: RHButtons.compactContentPadding,
    evaluationNotesFooterButtonFontSize: RHButtons.compactFontSize,
    evaluationNotesFooterButtonTextSafetyBuffer: RHSpacing.medium,
    evaluationNotesFooterButtonMinWidth: 110,
    evaluationNotesFooterButtonMaxWidth: 260,
    evaluationNotesFooterScrollBottomPadding: RHSpacing.large,
    evaluationNotesSaveButtonTitle: "Salvesta",
    evaluationNotesSaveButtonVariant: "success" as const,
    evaluationNotesCancelButtonTitle: "Loobu",
    evaluationNotesCancelButtonVariant: RHButtons.exitUtilityVariant,
    evaluationNotesDeleteButtonTitle: "Kustuta põhjendus",
    evaluationNotesDeleteButtonVariant: RHButtons.destructiveUtilityVariant,
    evaluationNotesCloseButtonTitle: "Sulge",
    evaluationNotesCloseButtonVariant: RHButtons.destructiveUtilityVariant,  // read-only close uses the shared soft-red language

    // question
    questionDialogMaxWidth: 920,
    questionDialogWidthViewportRatio: 0.9,
    questionDialogBorderRadius: `${RHRadius.large}px`,
    questionDialogBackgroundColor: UIColor.whiteColor,
    questionDialogBoxShadow: RHShadow.strong,
    get questionTitleTextViewType() { return UITextView.type.header4 },
    get questionTitleTextColor() { return RHColors.textPrimary },
    questionTitleBottomGap: RHSpacing.medium,
    questionQuestionHeaderHeight: 44,
    questionQuestionHeaderBackgroundColor: RHColors.slateGlass,
    questionQuestionHeaderBorderColor: RHColors.slateAlpha95,
    questionQuestionHeaderBorderRadius: `${RHRadius.medium}px`,
    questionQuestionHeaderHorizontalPadding: RHSpacing.small,
    questionQuestionHeaderVerticalPadding: RHSpacing.small,
    questionQuestionHeaderGap: RHSpacing.small,
    questionQuestionHeaderNameFontFamily: RHFont.familyBase,
    questionQuestionHeaderNameFontSize: RHFont.sizeMedium,
    questionQuestionHeaderNameFontWeight: RHFont.weightSemibold,
    questionQuestionHeaderNameTextColor: RHColors.textHeading,
    questionQuestionHeaderNameWidthSafetyBuffer: RHSpacing.medium,
    questionQuestionHeaderTimeFontFamily: RHFont.familyBase,
    questionQuestionHeaderTimeFontSize: RHFont.sizeSmall,
    questionQuestionHeaderTimeFontWeight: RHFont.weightMedium,
    questionQuestionHeaderTimeTextColor: RHColors.slateDark,
    questionQuestionHeaderTimeWidthSafetyBuffer: RHSpacing.medium,
    questionMetadataTopGap: RHSpacing.medium,
    questionMetadataRowHeight: 20,
    questionMetadataGap: RHSpacing.small,
    questionMetadataLabelFontFamily: RHFont.familyBase,
    questionMetadataLabelFontSize: RHFont.sizeSmall,
    questionMetadataLabelFontWeight: RHFont.weightRegular,
    questionMetadataLabelTextColor: RHColors.textHeading,
    questionMetadataLabelWidthSafetyBuffer: RHSpacing.medium,
    questionMetadataDateFontFamily: RHFont.familyBase,
    questionMetadataDateFontSize: 12,
    questionMetadataDateFontWeight: RHFont.weightMedium,
    questionMetadataDateTextColor: RHColors.slateMedium,
    questionMetadataDateWidthSafetyBuffer: RHSpacing.medium,
    questionQuestionBubbleTopGap: RHSpacing.small,
    questionQuestionBubbleWidthRatio: 1,
    questionQuestionBubbleMaxWidth: 1400,
    get questionAnswerSectionTextViewType() { return UITextView.type.header4 },
    get questionAnswerSectionTextColor() { return RHColors.textPrimary },
    questionAnswerSectionTopGap: RHSpacing.large,
    questionAnswerTextAreaTopGap: RHSpacing.small,
    questionAnswerTextAreaHeight: 176,
    questionFooterButtonHeight: RHButtons.compactPrimaryHeight,
    questionFooterButtonGap: RHSpacing.medium,
    get questionFooterButtonContentPadding() { return RHButtons.compactPrimaryContentPadding },
    get questionFooterButtonFontSize() { return RHButtons.compactPrimaryFontSize },
    questionFooterButtonTextSafetyBuffer: RHSpacing.medium,
    questionFooterButtonMinWidth: 180,
    questionFooterButtonMaxWidth: 250,
    questionFooterButtonsTopGap: RHSpacing.large,
    questionFooterBottomPadding: RHSpacing.large,
    questionCancelButtonVariant: RHButtons.destructiveUtilityVariant,
    questionSubmitButtonVariant: "success" as const,  // answer submission is a confirming action

}
